import TreeModel from 'tree-model';

import { ParentTypeEnum } from '../../generated/graphql';

const tree = new TreeModel();

interface ObjectType {
  id: string;
}

const AssessmentResults: TreeModel.Model<ObjectType>[] = [
  { id: ParentTypeEnum.TestResult },
  { id: ParentTypeEnum.RiskAssessmentResult },
  { id: ParentTypeEnum.ObligationAssessmentResult },
  { id: ParentTypeEnum.DocumentAssessmentResult },
];

const AssessmentHierarchy: TreeModel.Model<ObjectType> = {
  id: ParentTypeEnum.Assessment,
  children: [
    ...AssessmentResults,
    { id: ParentTypeEnum.Issue },
    { id: ParentTypeEnum.Action },
    { id: ParentTypeEnum.AssessmentActivity },
  ],
};

const ImpactHierarchy: TreeModel.Model<ObjectType> = {
  id: ParentTypeEnum.Impact,
  children: [{ id: ParentTypeEnum.ImpactRating }],
};

const ActionHierarchy: TreeModel.Model<ObjectType> = {
  id: ParentTypeEnum.Action,
  children: [{ id: ParentTypeEnum.ActionUpdate }],
};

const IssueHierarchy: TreeModel.Model<ObjectType> = {
  id: ParentTypeEnum.Issue,
  children: [
    { id: ParentTypeEnum.IssueUpdate },
    ActionHierarchy,
    { id: ParentTypeEnum.Cause },
    { id: ParentTypeEnum.Consequence },
    { id: ParentTypeEnum.IssueAssessment },
  ],
};

const IndicatorHierarchy: TreeModel.Model<ObjectType> = {
  id: ParentTypeEnum.Indicator,
  children: [{ id: ParentTypeEnum.IndicatorResult }],
};

const ControlHierarchy: TreeModel.Model<ObjectType> = {
  id: ParentTypeEnum.Control,
  children: [
    { id: ParentTypeEnum.TestResult },
    IssueHierarchy,
    ActionHierarchy,
    IndicatorHierarchy,
  ],
};

const ControlGroupHierarchy: TreeModel.Model<ObjectType> = {
  id: ParentTypeEnum.ControlGroup,
  children: [ControlHierarchy],
};

const ObligationHierarchy: TreeModel.Model<ObjectType> = {
  id: ParentTypeEnum.Obligation,
  children: [
    ControlHierarchy,
    ...AssessmentResults,
    { id: ParentTypeEnum.ObligationImpact },
    ActionHierarchy,
    IssueHierarchy,
  ],
};

const RiskHierarchy: TreeModel.Model<ObjectType> = {
  id: ParentTypeEnum.Risk,
  children: [
    ControlHierarchy,
    ...AssessmentResults,
    ImpactHierarchy,
    { id: ParentTypeEnum.Appetite },
    { id: ParentTypeEnum.Acceptance },
    ActionHierarchy,
    IndicatorHierarchy,
  ],
};

const ThirdPartyResponseHierarchy: TreeModel.Model<ObjectType> = {
  id: ParentTypeEnum.ThirdPartyResponse,
};

const QuestionnaireTemplateVersionHierarchy: TreeModel.Model<ObjectType> = {
  id: ParentTypeEnum.QuestionnaireTemplateVersion,
  children: [ThirdPartyResponseHierarchy],
};

const QuestionnaireTemplateHierarchy: TreeModel.Model<ObjectType> = {
  id: ParentTypeEnum.QuestionnaireTemplate,
  children: [QuestionnaireTemplateVersionHierarchy],
};

const ThirdPartyHierarchy: TreeModel.Model<ObjectType> = {
  id: ParentTypeEnum.ThirdParty,
  children: [
    ThirdPartyResponseHierarchy,
    ControlHierarchy,
    ActionHierarchy,
    IssueHierarchy,
  ],
};

const DocumentHierarchy: TreeModel.Model<ObjectType> = {
  id: ParentTypeEnum.Document,
  children: [
    ...AssessmentResults,
    ActionHierarchy,
    IssueHierarchy,
    {
      id: ParentTypeEnum.DocumentFile,
      children: [{ id: ParentTypeEnum.AttestationRecord }],
    },
  ],
};

const Root = tree.parse({
  id: 'root',
  children: [
    RiskHierarchy,
    ThirdPartyHierarchy,
    QuestionnaireTemplateHierarchy,
    ObligationHierarchy,
    DocumentHierarchy,
    ControlGroupHierarchy,
    AssessmentHierarchy,
  ],
});

export const isDescendant = (
  ancestorObjectType: ParentTypeEnum,
  descendantObjectType: ParentTypeEnum
): boolean => {
  const ancestorNode = Root.first(
    (node) => node.model.id === ancestorObjectType
  );
  if (!ancestorNode) {
    return false;
  }
  const descendantNode = ancestorNode.first((node) => {
    return node.model.id === descendantObjectType;
  });

  return !!descendantNode && descendantNode !== ancestorNode;
};
